// src/authpages/group/CreateGroupForm.js
import React, { useState } from 'react';
import { 
  Clock, 
  UserPlus, 
  CheckCircle, 
  UserCheck, 
  AlertCircle,
  Plus
} from 'lucide-react';

const CreateGroupForm = ({ 
  friends, 
  loading, 
  error, 
  studyMethods, 
  timerMethods,
  currentUserId,
  onCreateGroup 
}) => {
  // Form state
  const [groupName, setGroupName] = useState('');
  const [groupSubject, setGroupSubject] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedTimerMethod, setSelectedTimerMethod] = useState('');
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [formError, setFormError] = useState('');

  // Handle friend selection toggle
  const toggleFriendSelection = (friendId) => {
    setSelectedFriends(prev => {
      if (prev.includes(friendId)) {
        return prev.filter(id => id !== friendId);
      } else {
        return [...prev, friendId];
      }
    });
  };

  // Get selected timer method details
  const getSelectedTimerDetails = () => {
    if (!selectedTimerMethod) return null;
    return timerMethods.find(method => method.name === selectedTimerMethod);
  };

  const selectedTimer = getSelectedTimerDetails();

  // Form submission handler
  const handleSubmit = () => {
    // Reset any previous errors
    setFormError('');
    
    // Only require group name, subject, and friend selection
    if (!groupName || !groupSubject) {
      setFormError('Please fill in the group name and subject');
      return;
    }

    if (selectedFriends.length === 0) {
      setFormError('Please select at least one friend to add to your group');
      return;
    }

    // Get selected timer method details if one was selected
    let timerMethod = null;
    if (selectedTimerMethod) {
      timerMethod = timerMethods.find(method => method.name === selectedTimerMethod);
    }

    // Create chat room ID
    const chatRoomId = `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newGroup = {
      id: Date.now().toString(),
      name: groupName,
      subject: groupSubject,
      studyMethod: selectedMethod || 'Not specified', // Default value if none selected
      timerMethod: selectedTimerMethod || 'None', // Default value if none selected
      timerSettings: timerMethod || { 
        workTime: 25, 
        breakTime: 5 
      }, // Default timer settings if none selected
      createdAt: new Date().toISOString(),
      members: [currentUserId, ...selectedFriends],
      memberCount: 1 + selectedFriends.length,
      maxMembers: 8,
      creator: currentUserId,
      chatRoomId: chatRoomId,
      hasVideoEnabled: true,
      lastActive: new Date().toISOString()
    };

    // Call the parent component's handler
    onCreateGroup(newGroup);

    // Reset form
    setGroupName('');
    setGroupSubject('');
    setSelectedMethod('');
    setSelectedTimerMethod('');
    setSelectedFriends([]);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-blue-600">
        Create New Study Group
      </h2>
      
      {/* Error display */}
      {(formError || error) && (
        <div className="bg-red-50 p-4 rounded-md text-red-600 mb-4 flex items-center">
          <AlertCircle className="mr-2 flex-shrink-0" size={18} />
          <p>{formError || error}</p>
        </div>
      )}
      
      {/* Group Details */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Group Details</h3>
        <div className="space-y-4">
          <div>
            <label className="block mb-2 text-gray-700">Group Name</label>
            <input 
              type="text" 
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Enter group name"
              className="w-full p-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block mb-2 text-gray-700">Subject</label>
            <input 
              type="text" 
              value={groupSubject}
              onChange={(e) => setGroupSubject(e.target.value)}
              placeholder="What subject are you studying?"
              className="w-full p-2 border rounded-md"
            />
          </div>
        </div>
      </div>
      
      {/* Group Study Method Dropdown Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">
          Group Study Method <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </h3>
        <select 
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
          className="w-full p-2 border rounded-md"
        >
          <option value="">Select a study method (optional)</option>
          {studyMethods.map((method, index) => (
            <option key={index} value={method.name}>
              {method.name}
            </option>
          ))}
        </select>
        
        {selectedMethod && (
          <div className="mt-2 p-3 bg-blue-50 rounded-md">
            <p className="text-sm text-gray-600">
              {studyMethods.find(m => m.name === selectedMethod)?.description || ''}
            </p>
          </div>
        )}
      </div>
      
      {/* Timer Method Dropdown Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">
          Timer Method <span className="text-sm font-normal text-gray-500">(Optional)</span>
        </h3>
        <select 
          value={selectedTimerMethod}
          onChange={(e) => setSelectedTimerMethod(e.target.value)}
          className="w-full p-2 border rounded-md"
        >
          <option value="">Select a timer method (optional)</option>
          {timerMethods.map((timer, index) => (
            <option key={index} value={timer.name}>
              {timer.name}
            </option>
          ))}
        </select>
        
        {selectedTimer && (
          <div className="mt-2 p-3 bg-blue-50 rounded-md">
            <div className="flex items-center mb-2">
              <Clock className="mr-2 text-blue-500" size={18} />
              <h4 className="font-medium">{selectedTimer.name}</h4>
            </div>
            <p className="text-sm text-gray-600 mb-2">{selectedTimer.description}</p>
            <div className="text-xs text-gray-500">
              <span className="font-medium">Focus:</span> {selectedTimer.workTime}min / 
              <span className="font-medium"> Break:</span> {selectedTimer.breakTime}min
              {selectedTimer.longBreakTime && ` / `}
              {selectedTimer.longBreakTime && (
                <><span className="font-medium"> Long Break:</span> {selectedTimer.longBreakTime}min</>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Friend Selection */}
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3 text-gray-700">Add Friends (Verified Users Only)</h3>
        {loading ? (
          <p className="text-gray-600">Loading your friends...</p>
        ) : error ? (
          <div className="bg-red-50 p-3 rounded-md text-red-600 flex items-center">
            <AlertCircle className="mr-2" size={18} />
            {error}
          </div>
        ) : friends.length === 0 ? (
          <div className="bg-yellow-50 p-3 rounded-md text-yellow-700">
            <p>You don't have any verified friends yet. Add friends to create a study group.</p>
          </div>
        ) : (
          <div className="bg-gray-50 p-3 rounded-md max-h-60 overflow-y-auto">
            <p className="text-sm text-gray-500 mb-3">Select friends to add to your study group:</p>
            <div className="space-y-2">
              {friends.map((friend) => (
                <div 
                  key={friend.id}
                  className={`p-2 rounded-md flex items-center ${
                    selectedFriends.includes(friend.id) ? 'bg-blue-50' : 'hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    id={`friend-${friend.id}`}
                    checked={selectedFriends.includes(friend.id)}
                    onChange={() => toggleFriendSelection(friend.id)}
                    className="mr-3"
                  />
                  <label htmlFor={`friend-${friend.id}`} className="flex-grow cursor-pointer">
                    <div className="font-medium">{friend.fullName}</div>
                    <div className="text-sm text-gray-500">{friend.email}</div>
                  </label>
                  <CheckCircle 
                    className={`${selectedFriends.includes(friend.id) ? 'text-blue-500' : 'text-gray-200'}`} 
                    size={20} 
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-2 text-sm text-gray-500 flex items-center">
          <UserCheck className="mr-1" size={16} />
          {selectedFriends.length} friends selected
        </div>
      </div>
      
      {/* Group Features */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md">
        <h3 className="text-lg font-medium mb-2 text-blue-800">Group Features</h3>
        <p className="text-sm text-blue-600 mb-3">Your group will include:</p>
        <ul className="space-y-2">
          <li className="flex items-center text-gray-700">
            <CheckCircle className="mr-2 text-green-500" size={16} />
            Group chat for communication
          </li>
          <li className="flex items-center text-gray-700">
            <CheckCircle className="mr-2 text-green-500" size={16} />
            Video conferencing for face-to-face sessions
          </li>
          <li className="flex items-center text-gray-700">
            <CheckCircle className="mr-2 text-green-500" size={16} />
            Shared timer based on your selected method
          </li>
          <li className="flex items-center text-gray-700">
            <CheckCircle className="mr-2 text-green-500" size={16} />
            Progress tracking for group members
          </li>
        </ul>
      </div>
      
      {/* Create Button */}
      <button 
        onClick={handleSubmit}
        className="w-full bg-blue-500 text-white p-3 rounded-md hover:bg-blue-600 font-medium"
        disabled={loading || friends.length === 0}
      >
        <Plus className="inline mr-2" size={18} />
        Create Study Group
      </button>
    </div>
  );
};

export default CreateGroupForm;